import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import BusinessRegisterModal from '../components/forms/BusinessRegisterModal';
import AppDownloadSection from '../components/sections/AppDownloadSection';

import WaitlistModal from '../components/forms/WaitlistModal';

/* ── SVG Icons (matching design exactly) ── */
const Chk = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="8" fill="#E8F5E9" />
    <path d="M5 8l2 2 4-4" stroke="#1B5E40" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XMark = () => (
  <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">✗</span>
);

const Star = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="#BA7517" stroke="#BA7517" strokeWidth="0.5">
    <path d="M7 1l1.76 3.57 3.94.57-2.85 2.78.67 3.93L7 10.17l-3.52 1.68.67-3.93L1.3 5.14l3.94-.57L7 1z" />
  </svg>
);

const Home = () => {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [waitlistPlan, setWaitlistPlan] = useState(null);

  return (
    <div className="overflow-hidden">
      <HeroSection onRegisterClick={() => setIsRegisterModalOpen(true)} />
      <TrustBar />
      <BeforeAfterSection />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection
        onRegisterClick={() => setIsRegisterModalOpen(true)}
        onWaitlistClick={(planName) => setWaitlistPlan(planName)}
      />
      {/* <TestimonialSection /> */}
      <AppDownloadSection />
      <FinalCTASection onRegisterClick={() => setIsRegisterModalOpen(true)} />

      <BusinessRegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
      />
      <WaitlistModal
        isOpen={!!waitlistPlan}
        onClose={() => setWaitlistPlan(null)}
        planName={waitlistPlan || ''}
      />
    </div>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   HERO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const HeroSection = ({ onRegisterClick }) => (
  <section className="max-w-[1100px] mx-auto px-6 pt-28 pb-16">
    <div className="max-w-[680px]">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="font-serif text-4xl md:text-5xl font-bold leading-tight mb-5 tracking-tight text-warm-text"
      >
        Rent collection, leases,<br className="hidden sm:block" />
        and maintenance —<br className="hidden sm:block" />
        all in one app.
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="text-lg text-warm-textSub leading-relaxed mb-8 max-w-[540px]"
      >
        EzDaily gives landlords a dashboard and tenants a portal.
        No more scattered payments, lost lease documents, or
        maintenance requests buried in text messages.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex gap-3 mb-10 flex-wrap"
      >
        <button
          onClick={onRegisterClick}
          className="px-7 py-3.5 rounded-xl bg-brand text-white text-[15px] font-semibold hover:bg-brand-dark transition-colors flex items-center gap-2"
        >
          Register free <ArrowRight size={18} />
        </button>
        <a
          href="#features"
          className="px-7 py-3.5 rounded-xl border-[1.5px] border-warm-border text-warm-text text-[15px] font-medium hover:border-warm-textMuted transition-colors"
        >
          See how it works
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="flex gap-6 flex-wrap"
      >
        {[
          "Free to start. No per-unit fees.",
          "Tenants pay rent online — you receive the full amount",
          "Free tenant app included",
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[13px] text-warm-textSub">
            <Chk /> {item}
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   TRUST BAR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const TrustBar = () => (
  <section className="bg-warm-bgAlt border-y border-warm-border">
    <div className="max-w-[1100px] mx-auto px-6 py-5 flex items-center justify-center gap-12 flex-wrap">
      {[
        { top: "Zero platform fees on rent", bot: "Every plan, including free" },
        { top: "Landlord dashboard + tenant portal", bot: "Both included" },
        { top: "Works on iOS, Android, and web", bot: "For landlords and tenants" },
      ].map((item, i) => (
        <div key={i} className="text-center">
          <p className="text-sm font-semibold text-brand">{item.top}</p>
          <p className="text-[11px] text-warm-textMuted mt-0.5">{item.bot}</p>
        </div>
      ))}
    </div>
  </section>
);

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   BEFORE / AFTER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const BeforeAfterSection = () => {
  const before = [
    'Texting tenants "rent is due" every month',
    "Rent coming in through different payment apps and checks",
    "Leases tracked in spreadsheets or filing cabinets",
    "Maintenance requests lost in text messages",
    "No clear view of who has paid and who hasn't",
    "Move-in disputes with no documentation",
  ];
  const after = [
    "Automated rent reminders via email and push notifications",
    "Tenants pay online — full rent amount goes to your account",
    "Leases stored digitally, viewable by landlord and tenant",
    "Maintenance requests with photos and status tracking",
    "Dashboard shows payment status for every property",
    "Photo condition reports document everything at move-in",
  ];

  return (
    <section className="max-w-[1100px] mx-auto px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-serif text-3xl font-bold mb-3 text-warm-text">Sound familiar?</h2>
        <p className="text-base text-warm-textSub max-w-[460px] mx-auto">
          Most landlords are stuck juggling texts, spreadsheets, and payment apps.
        </p>
        <p className="text-base text-warm-textSub max-w-[460px] mx-auto">
          We built one place for everything.
        </p>        
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-[1fr_60px_1fr] gap-0 max-w-[950px] mx-auto"
      >
        {/* The old way */}
        <div className="bg-red-50 rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none p-7">
          <p className="text-xs font-bold tracking-wider uppercase text-red-600 mb-4">Without EzDaily</p>
          {before.map((item, i) => (
            <div key={i} className="flex items-start gap-2 mb-2.5 text-[13px] text-red-900/70 leading-relaxed">
              <XMark /> {item}
            </div>
          ))}
        </div>

        {/* Arrow */}
        <div className="hidden md:flex items-center justify-center bg-gradient-to-r from-red-50 to-brand-light">
          <div className="w-10 h-10 rounded-full bg-brand flex items-center justify-center text-white">
            <ArrowRight size={18} />
          </div>
        </div>

        {/* With EzDaily */}
        <div className="bg-brand-light rounded-b-2xl md:rounded-r-2xl md:rounded-bl-none p-7">
          <p className="text-xs font-bold tracking-wider uppercase text-brand mb-4">With EzDaily</p>
          {after.map((item, i) => (
            <div key={i} className="flex items-start gap-2 mb-2.5 text-[13px] text-brand-dark leading-relaxed">
              <Chk /> {item}
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   FEATURES (with Landlord / Tenant tabs)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const FeaturesSection = () => {
  const landlordFeatures = [
    { icon: "💰", title: "Rent collection", desc: "free online payments, autopay, auto-reminders, late fee tracking, partial payment blocking" },
    { icon: "🔧", title: "Maintenance", desc: "photo submissions from tenants, status tracking per unit, full repair history" },
    { icon: "💬", title: "Communication", desc: "timestamped messages, automated reminders, delivery confirmation", comingSoon: true },
    { icon: "🛏️", title: "Per-bed leasing", desc: "lease individual bedrooms, track occupancy per bed, manage roommate turnover independently" },
    { icon: "🔒", title: "Deposit management", desc: "track deposits, offer insurance alternatives, stay compliant with state rules" },
    { icon: "📄", title: "Lease storage", desc: "upload, e-sign, and access lease documents anytime" },
  ];

  const tenantFeatures = [
    { icon: "✅", title: "Pay rent free", desc: "no transaction fees on bank transfers, set up autopay once" },
    { icon: "📈", title: "Build your credit", desc: "on-time payments reported to credit bureaus, track your payment streak", comingSoon: true },
    { icon: "🔔", title: "See repair status", desc: "submit with photos, get live updates as your request progresses" },
    { icon: "🏠", title: "Get insured fast", desc: "buy renter's insurance in 2 minutes without leaving the app", comingSoon: true },
    { icon: "🛏️", title: "Pay your share", desc: "each roommate gets their own lease, their own payment, their own history" },
    { icon: "📋", title: "Everything in one place", desc: "lease, payments, messages, and important dates" },
  ];

  const ComingSoonPill = () => (
    <span className="inline-block text-[9px] font-medium tracking-wide text-warm-textMuted bg-warm-bgAlt border border-warm-border rounded-full px-1.5 py-[1px] leading-tight whitespace-nowrap ml-1.5">
      coming soon
    </span>
  );

  return (
    <section id="features" className="bg-warm-bgAlt border-y border-warm-border py-20">
      <div className="max-w-[1100px] mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-3xl font-bold mb-3 text-warm-text">
            Built for both sides of the lease
          </h2>
          <p className="text-base text-warm-textSub max-w-[540px] mx-auto">
            Most rental tools are built for landlords and give tenants a bare-bones portal. We designed for both.
          </p>
        </motion.div>

        {/* Two-card layout */}
        <div className="grid md:grid-cols-2 gap-5">
          {/* Landlord card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#F0FAF4] rounded-2xl p-7 border border-[#D1E9DA]"
          >
            <span className="inline-block text-[11px] font-semibold text-brand bg-white border border-[#D1E9DA] rounded-full px-3 py-1 mb-4">
              For landlords
            </span>
            <h3 className="font-serif text-[22px] font-bold text-warm-text mb-1.5 leading-snug">
              Run your rentals like a business
            </h3>
            <p className="text-[13px] text-warm-textSub mb-6 leading-relaxed">
              Everything after the lease is signed, without the enterprise price tag.
            </p>
            <div className="space-y-5">
              {landlordFeatures.map((feat, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-lg mt-0.5 w-6 text-center flex-shrink-0">{feat.icon}</span>
                  <p className="text-[13px] text-warm-textSub leading-relaxed">
                    <span className="font-semibold text-warm-text">{feat.title}</span>
                    {feat.comingSoon && <ComingSoonPill />}
                    {' — '}{feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Tenant card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-[#F4F0FA] rounded-2xl p-7 border border-[#DDD4EA]"
          >
            <span className="inline-block text-[11px] font-semibold text-[#5B3D8F] bg-white border border-[#DDD4EA] rounded-full px-3 py-1 mb-4">
              For tenants
            </span>
            <h3 className="font-serif text-[22px] font-bold text-warm-text mb-1.5 leading-snug">
              A rental app that works for you too
            </h3>
            <p className="text-[13px] text-warm-textSub mb-6 leading-relaxed">
              Pay rent, track repairs, build credit — with zero surprise fees.
            </p>
            <div className="space-y-5">
              {tenantFeatures.map((feat, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-lg mt-0.5 w-6 text-center flex-shrink-0">{feat.icon}</span>
                  <p className="text-[13px] text-warm-textSub leading-relaxed">
                    <span className="font-semibold text-warm-text">{feat.title}</span>
                    {feat.comingSoon && <ComingSoonPill />}
                    {' — '}{feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* AI coming soon banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-5 px-6 py-4 rounded-xl bg-warm-warningBg border border-[#EED9B7] flex items-center gap-3"
        >
          <span className="text-xl">🤖</span>
          <div>
            <p className="text-[13px] font-semibold text-warm-warning">AI-powered features coming soon</p>
            <p className="text-xs text-warm-textSub">
              An AI agent that handles tenant inquiries, drafts property listings, and learns your
              communication style — currently being tested with landlords. Join the Pro waitlist to get early access.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   HOW IT WORKS (4 steps)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const HowItWorksSection = () => {
  const steps = [
    { num: "1", title: "Register", desc: "Create your account and tell us about your rental business. Takes 2 minutes." },
    { num: "2", title: "Get verified", desc: "We verify your account to keep the platform trustworthy for landlords and tenants." },
    { num: "3", title: "Add properties", desc: "Enter your property details, upload photos, set rent amounts, and upload leases." },
    { num: "4", title: "Invite tenants", desc: "Send your tenants an invite link. They download the free app and they're connected to your property." },
  ];

  return (
    <section className="max-w-[1100px] mx-auto px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <h2 className="font-serif text-3xl font-bold mb-3 text-warm-text">Up and running in 15 minutes</h2>
        <p className="text-base text-warm-textSub max-w-[460px] mx-auto">
          Register, get verified, and start managing your properties. We can walk you through it on a call — or you can do it yourself.
        </p>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-[900px] mx-auto">
        {steps.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12 }}
            className="text-center"
          >
            <div className="w-11 h-11 rounded-full mx-auto mb-3.5 bg-brand-light border-2 border-brand flex items-center justify-center font-serif text-lg font-bold text-brand">
              {step.num}
            </div>
            <h3 className="text-[15px] font-bold mb-1.5 text-warm-text">{step.title}</h3>
            <p className="text-[13px] text-warm-textSub leading-relaxed">{step.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   PRICING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const PricingSection = ({ onRegisterClick, onWaitlistClick }) => {
  const tiers = [
    {
      name: "Starter", price: "$0", unit: "Free forever · Unlimited properties",
      badge: "Free forever", badgeStyle: "outline",
      desc: "Everything you need to manage your rentals professionally.",
      features: [
        { text: "Unlimited properties and units", bold: true },
        { text: "Rent collection — free ACH both sides", bold: true },
        "Per-bed leasing and rent splitting",
        "Automated rent reminders",
        "Late fee rules with grace periods",
        { text: "Maintenance requests with live status", comingSoon: true },
        { text: "Landlord–tenant messaging", comingSoon: true },
        "Store signed leases & invite tenants",
        { text: "Free tenant app — tenants manage rent & requests", bold: true },
        "Security deposit tracking",
        { text: "Renter's insurance marketplace", comingSoon: true },
        { text: "Build credit with on-time rent", comingSoon: true },
        "Rent payment history and tracking",
        "Free setup call — we help you get started",
      ],
      cta: "Get started free", featured: false,
    },
    {
      name: "Growth", price: "$29", unit: "/mo",
      subtitle: "Unlimited properties · 14-day free trial",
      badge: "Most popular", badgeStyle: "solid",
      subBadge: "For growing portfolios",
      desc: "Automation and advanced tools for landlords who need efficiency.",
      features: [
        { text: "Everything in Starter, plus:", bold: true },
        { text: "Next-day rent deposits", bold: true, comingSoon: true },
        { text: "Notices delivered via text + delivery proof", comingSoon: true },
        { text: "Assign repair vendors and track costs", comingSoon: true },
        "Move-in/move-out condition reports",
        { text: "Automated lease renewal reminders", bold: true, comingSoon: true },
        { text: "Bulk announcements to all tenants", comingSoon: true },
        { text: "Connect your bank — expenses auto-categorized", bold: true, comingSoon: true },
        { text: "Per-property income & expense reports", comingSoon: true },
        { text: "Ready-to-send landlord notice templates", comingSoon: true },
        { text: "Rental income dashboard", comingSoon: true },
      ],
      cta: "Join the waitlist", featured: true,
    },
    {
      name: "Pro", price: "$79", unit: "/mo",
      subtitle: "Unlimited properties",
      badge: "AI-powered", badgeStyle: "outline",
      desc: "AI management for larger portfolios. Your agent handles inquiries and drafts listings.",
      features: [
        { text: "Everything in Growth, plus:", bold: true },
        { text: "AI tenant inquiry agent", bold: true },
        "AI-drafted property listings",
        { text: "AI memory (learns your style)", italic: true },
        "Voice listing creation",
        "Tenant screening",
        "PDF + CSV export",
        "Revenue analytics",
        "Priority support",
        "Custom branded portal",
      ],
      cta: "Join the waitlist", featured: false,
    },
  ];

  return (
    <section id="pricing" className="bg-warm-bgAlt border-y border-warm-border py-20">
      <div className="max-w-[1100px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-3xl font-bold mb-3 text-warm-text">
            Simple pricing. Start free, upgrade when ready.
          </h2>
          <p className="text-base text-warm-textSub max-w-[460px] mx-auto">
            Unlimited properties on the free plan. No per-unit fees. No surprises.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 max-w-[840px] mx-auto">
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`bg-white rounded-2xl p-6 flex flex-col relative ${
                tier.featured
                  ? 'border-2 border-brand shadow-lg'
                  : 'border border-warm-border'
              }`}
            >
              {/* Badge */}
              {tier.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold px-3.5 py-1 rounded-full whitespace-nowrap ${
                  tier.badgeStyle === 'solid'
                    ? 'bg-brand text-white'
                    : 'bg-white border border-brand text-brand'
                }`}>
                  {tier.badge}
                </div>
              )}
              {/* Sub-badge */}
              {tier.subBadge && (
                <p className="text-[11px] text-warm-textMuted text-center mt-1 mb-0.5">{tier.subBadge}</p>
              )}
              <p className="text-sm font-bold text-center text-warm-text mt-1">{tier.name}</p>
              <div className="flex items-baseline justify-center gap-0.5 my-1">
                <p className="font-serif text-4xl font-bold text-warm-text">{tier.price}</p>
                {tier.unit.startsWith('/') && (
                  <p className="text-sm text-warm-textMuted">{tier.unit}</p>
                )}
              </div>
              {!tier.unit.startsWith('/') && (
                <p className="text-xs text-warm-textMuted text-center mb-1">{tier.unit}</p>
              )}
              {tier.subtitle && (
                <p className="text-xs text-warm-textMuted text-center mb-1">{tier.subtitle}</p>
              )}
              <p className="text-[13px] text-warm-textSub text-center mb-4 leading-relaxed">{tier.desc}</p>
              {(() => {
                const liveFeatures = tier.features.filter(f => !(typeof f === 'object' && f.comingSoon));
                const comingSoonFeatures = tier.features.filter(f => typeof f === 'object' && f.comingSoon);
                return (
                  <>
                    {/* What's included */}
                    <div className="border-t border-warm-border pt-3.5">
                      <p className="text-[10px] font-semibold tracking-wider text-warm-textMuted uppercase mb-2.5">What's included</p>
                      {liveFeatures.map((f, j) => {
                        const text = typeof f === 'string' ? f : f.text;
                        return (
                          <div key={`live-${j}`} className="flex items-start gap-1.5 text-xs text-warm-text mb-1.5">
                            <span className="mt-0.5 text-brand"><Chk /></span>
                            <span>{text}</span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Coming soon */}
                    {comingSoonFeatures.length > 0 && (
                      <div className="border-t border-dashed border-warm-border mt-3 pt-3">
                        <p className="text-[10px] font-semibold tracking-wider text-warm-textMuted uppercase mb-2.5">Coming soon</p>
                        {comingSoonFeatures.map((f, j) => {
                          const text = f.text;
                          return (
                            <div key={`soon-${j}`} className="flex items-start gap-1.5 text-xs text-warm-textMuted mb-1.5">
                              <span className="mt-0.5">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="opacity-50">
                                  <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1"/>
                                  <path d="M6 3.5V6L7.5 7.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
                                </svg>
                              </span>
                              <span>{text}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="flex-1" />
              <button
                onClick={() => {
                  if (tier.cta === 'Join the waitlist') {
                    onWaitlistClick(tier.name);
                  } else {
                    onRegisterClick();
                  }
                }}
                className={`w-full py-3 rounded-lg text-sm font-semibold mt-4 transition-colors ${
                  tier.featured
                    ? 'bg-brand text-white hover:bg-brand-dark'
                    : 'bg-transparent border-[1.5px] border-warm-border text-warm-text hover:border-warm-textMuted'
                }`}
              >
                {tier.cta}
              </button>
            </motion.div>
          ))}
        </div>

        {/* How rent payments work */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-[840px] mx-auto mt-6 px-5 py-4 rounded-xl bg-white border border-warm-border flex items-center gap-4"
        >
          <span className="text-2xl">💳</span>
          <div>
            <p className="text-[13px] font-semibold text-warm-text">How rent payments work</p>
            <p className="text-xs text-warm-textSub leading-relaxed">
              Tenants pay rent through the app by bank transfer or card. A standard processing fee applies
              at checkout — the same as any online payment. Landlords receive the full rent amount on every
              plan. EzDaily does not charge platform fees on rent.
            </p>
          </div>
        </motion.div>

        {/* Custom pricing */}
        <p className="text-center text-[13px] text-warm-textMuted mt-4">
          Need a custom setup for your portfolio?{' '}
          <a href="/contact" className="text-brand font-semibold hover:underline">Contact us — we'll help you get started.</a>
        </p>
      </div>
    </section>
  );
};

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   TESTIMONIAL
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
// const TestimonialSection = () => (
//   <section className="max-w-[1100px] mx-auto px-6 py-20">
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       whileInView={{ opacity: 1, y: 0 }}
//       viewport={{ once: true }}
//       className="max-w-[580px] mx-auto text-center"
//     >
//       <div className="flex justify-center gap-0.5 mb-4">
//         {[1, 2, 3, 4, 5].map((i) => (
//           <Star key={i} />
//         ))}
//       </div>
//       <p className="font-serif text-xl md:text-[22px] font-medium leading-relaxed mb-4 italic text-warm-text">
//         "I was managing 8 properties with a spreadsheet and three different payment apps.
//         Setting up EzDaily took one call. Now my tenants pay through the app and
//         I can see who's paid without checking five different places."
//       </p>
//       <p className="text-sm font-semibold text-warm-text">— Landlord, 8 properties</p>
//     </motion.div>
//   </section>
// );

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   FINAL CTA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
const FinalCTASection = ({ onRegisterClick }) => (
  <section className="max-w-[1100px] mx-auto px-6 py-20 text-center">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3 leading-snug text-warm-text">
        Ready to simplify how you manage rentals?
      </h2>
      <p className="text-base text-warm-textSub mb-7 max-w-[420px] mx-auto">
        Register free. Get verified. Start managing your properties in 15 minutes.
      </p>
      <button
        onClick={onRegisterClick}
        className="px-9 py-4 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors inline-flex items-center gap-2"
      >
        Register free <ArrowRight size={18} />
      </button>
    </motion.div>
  </section>
);

export default Home;
